import { Component } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { tap, first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { DictionaryItem, DictionaryItemDefinition, DictionarySimplePageComponent } from 'src/app/shared/components/dictionary-simple-page/dictionary-simple-page.component';
import { ApplicationViewModel, EsraPipingAPIClient } from 'src/app/shared/models/autogenerated-piping';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { ApplicationAddEditComponent } from '../dialogs/application-add-edit/application-add-edit.component';
import { DictionaryExportService } from '../services/dictionary-export.service';

@Component({
    selector: 'app-applications',
    templateUrl: '../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.html',
    styleUrls: ['../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.scss'],
    standalone: false
})
export class ApplicationsComponent extends DictionarySimplePageComponent<ApplicationViewModel> {
  constructor(
    private esraApiClient: EsraPipingAPIClient,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private insightsService: InsightsService,
    private dictionaryExportService: DictionaryExportService
  ) {
    super();
    this.exportFn = () => this.dictionaryExportService.exportApplications();
  }

  protected childOnInitBefore(): void { /* do nothing */ }
  protected childOnInitAfter(): void { /* do nothing */ }

  protected searchAll(): void {
    // Use the paged endpoint with showAll=true to load both active and inactive
    // records. The client-side filter predicate handles active/inactive visibility.
    // getAllCurrentApplications() only returns active items (it is a dropdown feed)
    // and would hide inactive records from the admin grid entirely.
    this.esraApiClient.getAllApplicationsPaged(undefined, undefined, undefined, true)
      .pipe(
        indicate(this.searching$),
        first(),
        tap((applications: ApplicationViewModel[]) => {
          this.totalCount = applications.length;
          this.dataSource.data = applications;
        })
      ).subscribe();
  }

  protected initializeDictionaryDefinition(): DictionaryItemDefinition<ApplicationViewModel> {
    return {
      displayStatusColumn: true,
      displayDeleteButton: true,
      dictionaryItems: [
        new DictionaryItem('Last Updated', 'updated',   'standard', (item: ApplicationViewModel) => this.getDate(item)),
        new DictionaryItem('Application',  'name',      'extended'),
        new DictionaryItem('Short Name',   'shortName', 'extended')
      ]
    };
  }

  protected getDate(item: ApplicationViewModel): string {
    return String(item.updated.getMonth() + 1).padStart(2, '0') + '/' +
           String(item.updated.getDate()).padStart(2, '0') + '/' +
           item.updated.getFullYear();
  }

  protected getFilterPredicate(record: ApplicationViewModel, parametersMap: Map<string, string>): boolean {
    const searchQuery: string = parametersMap.get(DictionarySimplePageComponent.SEARCH_QUERY_NAME)?.toLowerCase() || '';
    const isActiveFilter: string = parametersMap.get(DictionarySimplePageComponent.IS_ACTIVE_NAME);

    const matchesSearchQuery: boolean =
      (record.name?.toLowerCase().includes(searchQuery) ?? false) ||
      (record.shortName?.toLowerCase().includes(searchQuery) ?? false) ||
      record.updated.toString().toLowerCase().includes(searchQuery);

    if (isActiveFilter == null) {
      return matchesSearchQuery;
    } else {
      return matchesSearchQuery && record.isActive === JSON.parse(isActiveFilter);
    }
  }

  protected getNoMatchMessage(): string {
    return 'No applications match provided search criteria';
  }

  protected createNew(): void {
    this.openEditor(null);
  }

  protected editItem(application: ApplicationViewModel): void {
    this.openEditor(application);
  }

  protected editItemReferenced(application: ApplicationViewModel): void {
    // Referenced applications can still be edited — only deactivation is blocked.
    this.editItem(application);
  }

  private openEditor(application: ApplicationViewModel | null): void {
    const dialogRef: MatDialogRef<ApplicationAddEditComponent, boolean> = this.dialog.open<
      ApplicationAddEditComponent,
      ApplicationViewModel | null,
      boolean
    >(ApplicationAddEditComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: true,
      data: application
    });

    dialogRef.afterClosed().pipe(first()).subscribe((saved: boolean | undefined) => {
      if (saved) {
        this.searchAll();
      }
    });
  }

  protected deleteItem(item: ApplicationViewModel): void {
    const hasAssociations = (item as any)['hasAssociations'];

    if (hasAssociations) {
      this.snackBarService.showSnackBar(
        true,
        `Application "${item.name}" could not be deleted because it has associated items.`,
        'mat-snack-bar-error'
      );
      return;
    }

    this.esraApiClient.deleteApplication(item.id).subscribe({
      next: () => {
        const index: number = this.dataSource.data.findIndex(a => a.id === item.id);
        if (index !== -1) {
          this.dataSource.data.splice(index, 1);
          this.dataSource.data = [...this.dataSource.data];
        }
        this.snackBarService.showSnackBar(
          true,
          `Application "${item.name}" was successfully deleted.`,
          'mat-snack-bar-info'
        );
      },
      error: (error) => {
        this.snackBarService.showSnackBar(
          true,
          `Sorry, something went wrong deleting application "${item.name}".`,
          'mat-snack-bar-error'
        );
        this.insightsService.logException(error);
      }
    });
  }

  protected updateStatus(item: ApplicationViewModel): void {
    this.esraApiClient.toggleApplicationStatus(item.id).subscribe({
      next: (updated: ApplicationViewModel) => {
        const index: number = this.dataSource.data.findIndex(a => a.id === item.id);
        if (index !== -1) {
          this.dataSource.data[index] = updated;
          this.dataSource.data = [...this.dataSource.data];
        }
        const newState: string = item.isActive ? 'deactivated' : 'activated';
        this.snackBarService.showSnackBar(
          true,
          `Application "${item.name}" was successfully ${newState}.`,
          'mat-snack-bar-info'
        );
        this.search();
      },
      error: (error) => {
        this.snackBarService.showSnackBar(
          true,
          `Sorry, something went wrong toggling application "${item.name}".`,
          'mat-snack-bar-error'
        );
        this.insightsService.logException(error);
      }
    });
  }

  protected getUsageInformation(_item: object): void { /* not applicable for applications */ }
}
